/* Bar chart: one metric per day, optionally split by Seller or revenue type.
   The total and split sit beside the chart when it is wide (≥ 620px) and on one
   line above it when narrow; narrow charts show the most recent bars only. */

import { useTranslation } from 'react-i18next';
import { C } from '../../components/ChatAssistant/compact/tokens';
import { font as f } from '../../components/ChatAssistant/compact/style';
import {
  CREDITED, SELLERS, fmt, pick, shortDay, typeColor, typeOrder, usd,
} from '../../components/Statistics/unified/model';
import { periodRange, previousRange, useDash, useSource } from '../context';
import { effectiveSplit, type WidgetOf } from '../model';
import { WidgetFrame, type FrameProps } from '../WidgetFrame';
import { bodySize, useDataState } from '../frameUtils';
import { change, sellerFill, useMetaText } from './shared';

interface Key { id: string; label: string; color: string }
interface Bar { day: string; values: number[] }

export function BarChart(p: FrameProps & { widget: WidgetOf<'chart'> }) {
  const { t, i18n } = useTranslation('dashboardWidgets');
  const { t: ts } = useTranslation('statsUnified');
  const { today, money, editing } = useDash();
  const meta = useMetaText();
  const state = useDataState();
  const c = p.widget.cfg;
  const split = effectiveSplit(c);
  const isMoney = c.metric === 'rev' || c.metric === 'ppv';
  const range = periodRange(c.per, today);
  const stats = useSource('stats', isMoney ? range : null);
  const chat = useSource('chat', isMoney ? null : range);
  const prevStats = useSource('stats', c.summary && c.metric === 'ppv' ? previousRange(range) : null);
  const prevChat = useSource('chat', c.summary && !isMoney ? previousRange(range) : null);
  const src = isMoney ? stats : chat;

  /* ── Series ─────────────────────────────────────────────────────── */
  let keys: Key[] = [{ id: 'total', label: t(`chart.metric.${c.metric}`), color: C.purple }];
  let bars: Bar[] = [];
  let previous: number | null = null;
  /** Revenue's total in the other money mode (headline Revenue shows both). */
  let otherTotal = '';
  if (isMoney && stats.data) {
    const days = stats.data.money.days;
    if (c.metric === 'rev') {
      if (split === 'seller') keys = SELLERS.map((k) => ({ id: k, label: ts(`sellers.${k}`), color: sellerFill(k) }));
      if (split === 'type') {
        const types = [...new Set(days.flatMap((d) => Object.keys(d.by_type)))].sort((a, b) => typeOrder(a) - typeOrder(b));
        keys = types.map((id) => ({ id, label: ts(`types.${id}`, { defaultValue: id }), color: typeColor(id) }));
      }
      bars = days.map((d) => ({
        day: d.day,
        values: split === 'seller' ? SELLERS.map((k) => pick(d.sellers[k], money))
          : split === 'type' ? keys.map((k) => pick(d.by_type[k.id], money))
          : [pick(d.revenue, money)],
      }));
      previous = pick(stats.data.money.previous, money);
      const other = money === 'gross' ? 'net' : 'gross';
      otherTotal = `${ts(`money.${other}`)} ${usd(pick(stats.data.money.revenue, other))}`;
    } else {
      if (split === 'seller') keys = CREDITED.map((k) => ({ id: k, label: ts(`sellers.${k}`), color: sellerFill(k) }));
      bars = days.map((d) => ({ day: d.day, values: split === 'seller' ? CREDITED.map((k) => d.ppv_sold[k] ?? 0) : [CREDITED.reduce((a, k) => a + (d.ppv_sold[k] ?? 0), 0)] }));
      if (prevStats.data) previous = prevStats.data.money.days.reduce((a, d) => a + CREDITED.reduce((s, k) => s + (d.ppv_sold[k] ?? 0), 0), 0);
    }
  } else if (!isMoney && chat.data) {
    const val = (d: { chats_activated: number; messages_sent: number; follow_ups_sent: number }) =>
      c.metric === 'chats' ? d.chats_activated : d.messages_sent + d.follow_ups_sent;
    bars = chat.data.days.map((d) => ({ day: d.date, values: [val(d)] }));
    if (prevChat.data) previous = val(prevChat.data.totals);
  }
  const value = (v: number) => (c.metric === 'rev' ? usd(v) : fmt(v));

  /* ── Fitting ────────────────────────────────────────────────────── */
  const { width, height } = bodySize(p);
  const side = c.summary && width >= 620;
  const top = c.summary && !side;
  const chartW = width - 32 - (side ? 256 : 0);
  const maxBars = Math.max(4, Math.floor((chartW + 3) / 13));
  const shown = bars.slice(-Math.min(bars.length, maxBars));
  const chartH = Math.max(30, height - 28 - 18 - 14 - 16);
  const peak = Math.max(1, ...shown.map((b) => b.values.reduce((a, x) => a + x, 0)));
  const every = Math.ceil(shown.length / Math.max(2, Math.floor(chartW / 64)));
  const weekday = new Intl.DateTimeFormat(i18n.language, { weekday: 'short', timeZone: 'UTC' });
  const tick = (iso: string) => (c.per === 1 ? weekday.format(new Date(`${iso}T00:00:00Z`)) : shortDay(iso));

  const sums = keys.map((_, i) => bars.reduce((a, b) => a + (b.values[i] ?? 0), 0));
  const total = sums.reduce((a, x) => a + x, 0);
  const delta = change(total, previous);
  const legend = keys.map((k, i) => ({ ...k, sum: sums[i] ?? 0, pct: total ? Math.round(((sums[i] ?? 0) / total) * 100) : 0 }));
  const legendRows = legend.slice(0, Math.max(1, Math.floor((height - 28 - 92) / 22)));

  const metaText = shown.length < bars.length
    ? t('chart.lastOf', { n: shown.length, m: bars.length })
    : t('chart.byDay', { period: meta.period(c.per) });
  const note = state({ error: src.error, noAccess: src.noAccess, hasData: !!src.data, reload: src.reload });

  return (
    <WidgetFrame {...p} meta={meta.join([metaText], isMoney)} link={{ to: '/statistics', label: t('chart.link'), minWidth: 520 }}>
      {note ?? (
        <div style={{ flex: 1, minHeight: 0, padding: '14px 16px', display: 'flex', gap: 24, boxSizing: 'border-box' }}>
          {side && (
            <div style={{ width: 232, flex: 'none', display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0 }}>
              <span style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={{ font: f(500, 12), color: C.sub }}>{meta.period(c.per)}</span>
                <span style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ font: f(700, 32, 1), color: C.text }}>{value(total)}</span>
                  {delta && <span style={{ font: f(600, 12.5), color: delta.startsWith('−') ? C.sub : C.purpleLt }}>{delta}</span>}
                </span>
                {otherTotal && <span style={{ font: f(500, 11.5), color: C.muted }}>{otherTotal}</span>}
              </span>
              {keys.length > 1 && total > 0 && (
                <span style={{ display: 'flex', height: 8, borderRadius: 99, overflow: 'hidden', gap: 2 }}>
                  {legend.filter((s) => s.sum > 0).map((s) => <span key={s.id} style={{ width: `${(s.sum / total) * 100}%`, background: s.color }} />)}
                </span>
              )}
              {keys.length > 1 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {legendRows.map((s) => (
                    <span key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, font: f(400, 12.5), color: C.body }}>
                      <span style={{ width: 8, height: 8, flex: 'none', borderRadius: 2, background: s.color }} />
                      <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.label}</span>
                      <span style={{ font: f(600, 12.5), color: C.text }}>{value(s.sum)}</span>
                      <span style={{ width: 34, textAlign: 'right', color: C.muted }}>{s.pct}%</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ height: 18, flex: 'none', display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, overflow: 'hidden' }}>
              {top && (
                <>
                  <span style={{ font: f(700, 15), color: C.text, whiteSpace: 'nowrap' }}>{value(total)}</span>
                  {delta && <span style={{ font: f(600, 12), color: delta.startsWith('−') ? C.sub : C.purpleLt, whiteSpace: 'nowrap' }}>{delta}</span>}
                </>
              )}
              <span style={{ flex: 1 }} />
              {keys.length > 1 && legend.map((s) => (
                <span key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 6, font: f(400, 11.5), color: C.sub, whiteSpace: 'nowrap' }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: s.color }} />{s.label}
                </span>
              ))}
            </div>
            <div style={{ height: chartH, flex: 'none', display: 'flex', alignItems: 'flex-end', gap: 3, borderBottom: `1px solid ${C.borderHi}` }}>
              {shown.map((b) => {
                const tip = [tick(b.day), ...keys.map((k, i) => `${keys.length > 1 ? `${k.label} ` : ''}${value(b.values[i] ?? 0)}`)].join(' · ');
                return (
                  <span key={b.day} title={editing ? undefined : tip} className="dbw-bar-seg" style={{ flex: 1, minWidth: 0, height: '100%', display: 'flex', flexDirection: 'column-reverse', gap: 1 }}>
                    {b.values.map((v, i) => v > 0 && (
                      <span key={keys[i]?.id ?? i} style={{ flex: 'none', height: Math.max(1, Math.round((v / peak) * chartH)), background: keys[i]?.color ?? C.purple }} />
                    ))}
                  </span>
                );
              })}
            </div>
            <div style={{ height: 14, flex: 'none', display: 'flex', gap: 3 }}>
              {shown.map((b, k) => (
                <span key={b.day} style={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'center', overflow: 'visible', font: f(400, 10.5), color: C.muted, whiteSpace: 'nowrap' }}>
                  {(shown.length - 1 - k) % every === 0 ? tick(b.day) : ''}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </WidgetFrame>
  );
}
